import type { Document } from '@agentwiki/shared'
import { getDocumentKindInfo } from '../../lib/document-kind'
import { getDocumentSourceIdentity } from '../../lib/document-source-identity'

export interface ProjectTreeNode {
  id: string
  name: string
  children: ProjectTreeNode[]
  doc?: Document
}

export interface WorkspaceKnowledgeProject {
  id: string
  label: string
  subtitle: string
  docs: Document[]
  root: ProjectTreeNode[]
  verifiedCount: number
  reviewCount: number
}

interface DraftProject {
  id: string
  label: string
  subtitle: string
  docs: Document[]
  root: ProjectTreeNode[]
}

export function workspaceDocPath(doc: Document) {
  return `/doc/${doc.slug || doc.id}`
}

function projectIdentity(doc: Document) {
  const source = doc.sourceProvenance
  if (!source) return null
  const identity = getDocumentSourceIdentity(source)
  return {
    id: identity.key,
    label: identity.label,
    subtitle:
      source.sourceType === 'repo' && !source.repoUrl
        ? 'source-backed docs without repo metadata'
        : source.branch
          ? `${identity.kindLabel.toLowerCase()} source · ${source.branch}`
          : `${identity.kindLabel.toLowerCase()} source`,
  }
}

function sourceSegments(doc: Document) {
  const sourcePath = doc.sourceProvenance?.sourcePath || doc.title || 'Untitled'
  return sourcePath
    .split('/')
    .map((part) => part.trim())
    .filter(Boolean)
}

function upsertNode(nodes: ProjectTreeNode[], name: string, id: string) {
  let node = nodes.find((item) => item.name === name)
  if (!node) {
    node = { id, name, children: [] }
    nodes.push(node)
  }
  return node
}

function insertDoc(root: ProjectTreeNode[], doc: Document) {
  const segments = sourceSegments(doc)
  let cursor = root
  let path = ''
  segments.forEach((segment, index) => {
    path = path ? `${path}/${segment}` : segment
    const node = upsertNode(cursor, segment, `${doc.id}:${path}`)
    if (index === segments.length - 1) node.doc = doc
    cursor = node.children
  })
}

function sortTree(nodes: ProjectTreeNode[]): ProjectTreeNode[] {
  return nodes
    .map((node) => ({ ...node, children: sortTree(node.children) }))
    .sort((a, b) => {
      if (!!a.doc !== !!b.doc) return a.doc ? 1 : -1
      return a.name.localeCompare(b.name)
    })
}

export function buildWorkspaceKnowledgeProjects(
  documents: Document[],
): WorkspaceKnowledgeProject[] {
  const projectMap = new Map<string, DraftProject>()

  for (const doc of documents) {
    const identity = projectIdentity(doc)
    if (!identity) continue
    const project = projectMap.get(identity.id) ?? {
      ...identity,
      docs: [],
      root: [],
    }
    project.docs.push(doc)
    insertDoc(project.root, doc)
    projectMap.set(identity.id, project)
  }

  return Array.from(projectMap.values())
    .map((project) => {
      const reviewCount = project.docs.filter((doc) => {
        const kind = getDocumentKindInfo(doc).kind
        return kind === 'stale' || kind === 'conflict'
      }).length
      return {
        ...project,
        root: sortTree(project.root),
        verifiedCount: project.docs.filter((doc) => getDocumentKindInfo(doc).kind === 'verified')
          .length,
        reviewCount,
      }
    })
    .sort((a, b) => b.docs.length - a.docs.length || a.label.localeCompare(b.label))
}
